<template>
  <CardLayoutV2 title="Frequency of Visits">
    <div class="d-flex justify-end align-center">
      <DateTimeFilter
        v-model="dateTime"
        :ranges="['1m', '2m', '3m']"
        default="1m"
        custom
        class="black-border"
      />
    </div>
    <v-skeleton-loader
      v-if="loading"
      class="mt-4"
      type="image, image"
      height="380"
    />
    <div v-else-if="!chartData.datasets.length">
      <em class="mt-10"> No Data Available </em>
    </div>
    <div v-else style="cursor: pointer">
      <PieChart
        class="mt-10"
        v-if="chartData.datasets.length"
        :chartData="chartData"
        :redirectPath="{ name: 'FacilityVisitsReport' }"
        :date-time="{
          after: dateTime.after,
          before: dateTime.before,
          range: dateTime.range,
        }"
      />
    </div>
  </CardLayoutV2>
</template>

<script>
import PieChart from "@components/dashboard/operation_and_security/PieChart.vue";
import CardLayoutV2 from "@layout/CardLayoutV2.vue";
import { ref, computed } from "vue";
import useDashboard from "../useDashboard";
import useUser from "@components/authentication/useUser";
import { formatToISODate, getMonth } from "@tod-ui/helpers/datetime";
import { FACILITY_VISITS_REPORT } from "./graphql";
import { FREQUENCY_VISITS } from "@tod-ui/constants/dashboard";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";

export default {
  name: "CardAverageNumberOfVisits",
  components: {
    PieChart,
    CardLayoutV2,
    DateTimeFilter,
  },
  setup() {
    const { defaultOrganization } = useUser();
    const { selectedFacilities } = useDashboard();

    const currentMonth = computed(() =>
      getMonth(0, 0, defaultOrganization.value.timeZone)
    );

    const currentDate = computed(() =>
      formatToISODate(
        new Date().toISOString(),
        defaultOrganization.value.timeZone
      )
    );

    const dateTime = ref({
      after: currentMonth.value,
      before: currentDate.value,
    });

    const formattedDate = computed(() => ({
      after: formatToISODate(
        dateTime.value.after,
        defaultOrganization.value.timeZone
      ),
      before: formatToISODate(
        dateTime.value.before,
        defaultOrganization.value.timeZone
      ),
    }));

    return {
      selectedFacilities,
      dateTime,
      formattedDate,
    };
  },
  data() {
    return {
      visitsData: [],
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.visitsData.loading;
    },
    chartData() {
      if (!this.visitsData.length) {
        return { labels: [], datasets: [] };
      }

      const frequencies = {
        [FREQUENCY_VISITS.LESS_THAN_1]: 0,
        [FREQUENCY_VISITS.BETWEEN_1_AND_2]: 0,
        [FREQUENCY_VISITS.BETWEEN_3_AND_5]: 0,
        [FREQUENCY_VISITS.BETWEEN_6_AND_8]: 0,
        [FREQUENCY_VISITS.GREATER_THAN_9]: 0,
      };

      this.visitsData.forEach(({ visitsNumber }) => {
        if (visitsNumber === 0) frequencies[FREQUENCY_VISITS.LESS_THAN_1]++;
        else if (visitsNumber <= 2)
          frequencies[FREQUENCY_VISITS.BETWEEN_1_AND_2]++;
        else if (visitsNumber <= 5)
          frequencies[FREQUENCY_VISITS.BETWEEN_3_AND_5]++;
        else if (visitsNumber <= 8)
          frequencies[FREQUENCY_VISITS.BETWEEN_6_AND_8]++;
        else frequencies[FREQUENCY_VISITS.GREATER_THAN_9]++;
      });

      return {
        labels: Object.keys(frequencies),
        datasets: [
          {
            data: Object.values(frequencies),
            backgroundColor: [
              "rgb(0, 132, 255)",
              "rgb(10, 199, 60)",
              "rgb(249, 203, 0)",
              "rgb(255, 158, 0)",
              "rgb(255, 85, 51)",
            ],
          },
        ],
      };
    },
  },
  apollo: {
    visitsData: {
      query: FACILITY_VISITS_REPORT,
      variables() {
        return {
          facilityIds: this.selectedFacilities.map((f) => f.id),
          dateFilter: this.formattedDate,
        };
      },
      update: (data) => data.facilityVisitsReport,
      skip() {
        return this.selectedFacilities.length === 0 || !this.dateTime.after;
      },
    },
  },
};
</script>
